import { AppError } from "../../../shared/utils/error-handler";
import type {
  TodoTemplateRepository,
  OrderTodoRepository,
} from "../../repositories/company/todo-repository";
import type {
  TodoTemplate,
  CreateTodoTemplateRequest,
  UpdateTodoTemplateRequest,
  ReorderTodoTemplatesRequest,
  OrderTodo,
  UpdateTodoStatusRequest,
  UpdateTodoAssigneesRequest,
  AddTodoNoteRequest,
  TodoNote,
  CompanyTodosFilter,
} from "../../types/todo";

// ========== Todo Template UseCase ==========

export class TodoTemplateUseCase {
  constructor(private todoTemplateRepository: TodoTemplateRepository) {}

  async getTemplates(companyId: number, productId: number): Promise<TodoTemplate[]> {
    try {
      return await this.todoTemplateRepository.getTemplates(companyId, productId);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿 목록을 불러오지 못했습니다.",
        "TEMPLATES_GET_FAILED",
        error
      );
    }
  }

  async getTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<TodoTemplate> {
    try {
      return await this.todoTemplateRepository.getTemplate(companyId, productId, templateId);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿을 불러오지 못했습니다.",
        "TEMPLATE_GET_FAILED",
        error
      );
    }
  }

  async createTemplate(
    companyId: number,
    productId: number,
    request: CreateTodoTemplateRequest
  ): Promise<TodoTemplate> {
    try {
      return await this.todoTemplateRepository.createTemplate(companyId, productId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿을 생성하지 못했습니다.",
        "TEMPLATE_CREATE_FAILED",
        error
      );
    }
  }

  async updateTemplate(
    companyId: number,
    productId: number,
    templateId: number,
    request: UpdateTodoTemplateRequest
  ): Promise<TodoTemplate> {
    try {
      return await this.todoTemplateRepository.updateTemplate(
        companyId,
        productId,
        templateId,
        request
      );
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿을 수정하지 못했습니다.",
        "TEMPLATE_UPDATE_FAILED",
        error
      );
    }
  }

  async reorderTemplates(
    companyId: number,
    productId: number,
    request: ReorderTodoTemplatesRequest
  ): Promise<TodoTemplate[]> {
    try {
      return await this.todoTemplateRepository.reorderTemplates(companyId, productId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿 순서를 변경하지 못했습니다.",
        "TEMPLATES_REORDER_FAILED",
        error
      );
    }
  }

  async toggleActive(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<TodoTemplate> {
    try {
      return await this.todoTemplateRepository.toggleActive(companyId, productId, templateId);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿 활성화 상태를 변경하지 못했습니다.",
        "TEMPLATE_TOGGLE_FAILED",
        error
      );
    }
  }

  async deleteTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<boolean> {
    try {
      return await this.todoTemplateRepository.deleteTemplate(companyId, productId, templateId);
    } catch (error) {
      throw new AppError(
        String(error) || "템플릿을 삭제하지 못했습니다.",
        "TEMPLATE_DELETE_FAILED",
        error
      );
    }
  }
}

// ========== Order Todo UseCase ==========

export class OrderTodoUseCase {
  constructor(private orderTodoRepository: OrderTodoRepository) {}

  async getOrderTodos(companyId: number, orderId: number): Promise<OrderTodo[]> {
    try {
      return await this.orderTodoRepository.getOrderTodos(companyId, orderId);
    } catch (error) {
      throw new AppError(
        String(error) || "주문 To-Do 목록을 불러오지 못했습니다.",
        "ORDER_TODOS_GET_FAILED",
        error
      );
    }
  }

  async getOrderTodo(
    companyId: number,
    orderId: number,
    todoId: number
  ): Promise<OrderTodo> {
    try {
      return await this.orderTodoRepository.getOrderTodo(companyId, orderId, todoId);
    } catch (error) {
      throw new AppError(
        String(error) || "To-Do를 불러오지 못했습니다.",
        "TODO_GET_FAILED",
        error
      );
    }
  }

  async getCompanyTodos(
    companyId: number,
    filter?: CompanyTodosFilter
  ): Promise<OrderTodo[]> {
    try {
      return await this.orderTodoRepository.getCompanyTodos(companyId, filter);
    } catch (error) {
      throw new AppError(
        String(error) || "회사 To-Do 목록을 불러오지 못했습니다.",
        "COMPANY_TODOS_GET_FAILED",
        error
      );
    }
  }

  async getMyTodos(companyId: number): Promise<OrderTodo[]> {
    try {
      return await this.orderTodoRepository.getMyTodos(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "내 To-Do 목록을 불러오지 못했습니다.",
        "MY_TODOS_GET_FAILED",
        error
      );
    }
  }

  async updateStatus(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoStatusRequest
  ): Promise<OrderTodo> {
    try {
      return await this.orderTodoRepository.updateStatus(companyId, orderId, todoId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "To-Do 상태를 변경하지 못했습니다.",
        "TODO_STATUS_UPDATE_FAILED",
        error
      );
    }
  }

  async updateAssignees(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoAssigneesRequest
  ): Promise<OrderTodo> {
    try {
      return await this.orderTodoRepository.updateAssignees(companyId, orderId, todoId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "담당자를 변경하지 못했습니다.",
        "TODO_ASSIGNEES_UPDATE_FAILED",
        error
      );
    }
  }

  async addNote(
    companyId: number,
    orderId: number,
    todoId: number,
    request: AddTodoNoteRequest
  ): Promise<TodoNote> {
    try {
      return await this.orderTodoRepository.addNote(companyId, orderId, todoId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "메모를 추가하지 못했습니다.",
        "TODO_NOTE_ADD_FAILED",
        error
      );
    }
  }
}
